import React, { useState, useEffect } from "react";
import emailjs from '@emailjs/browser';
import './Portfolio.css';

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState('about');
  const [navOpen, setNavOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  // Initialize EmailJS
  React.useEffect(() => {
    try {
      emailjs.init('x003gC8KtN2ZS2gKb');
      console.log('EmailJS initialized successfully');
    } catch (error) {
      console.error('Failed to initialize EmailJS:', error);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setNavOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    // Validate form data
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus('error');
      console.error('All fields are required');
      setTimeout(() => setFormStatus(''), 5000);
      return;
    }

    try {
      // EmailJS configuration
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_name: 'Selvakumar',
        to_email: 'selva.enquiry@gmail.com',
        reply_to: formData.email,
        user_email: formData.email,
        user_name: formData.name,
        user_subject: formData.subject,
        user_message: formData.message
      };

      // EmailJS credentials
      const serviceId = 'service_144wbcg';
      const templateId = 'template_bobzjdz';
      const publicKey = 'x003gC8KtN2ZS2gKb';

      console.log('Attempting to send email with params:', templateParams);
      
      // Use emailjs.send with public key parameter
      const response = await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      console.log('SUCCESS!', response.status, response.text);
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setFormStatus(''), 5000);
      
    } catch (error) {
      console.error('EmailJS Error Details:', error);
      console.error('Error status:', error.status);
      console.error('Error text:', error.text);
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 5000);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="portfolio-container">
        {/* Top Navigation */}
        <header className="top-nav">
          <div className="top-nav-content">
            <div className="logo-small">&lt;SELVAKUMAR PANNEERSELVAM/&gt;</div>
            <nav className="top-nav-menu">
              <ul className="top-nav-links hide-on-mobile">
                <li className={activeSection === 'about' ? 'active' : ''}>
                  <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>
                    <span className="nav-icon">👤</span>
                    <span className="nav-text">About</span>
                  </a>
                </li>
                <li className={activeSection === 'experience' ? 'active' : ''}>
                  <a href="#experience" onClick={(e) => { e.preventDefault(); scrollToSection('experience'); }}>
                    <span className="nav-icon">💼</span>
                    <span className="nav-text">Experience</span>
                  </a>
                </li>
                <li className={activeSection === 'skills' ? 'active' : ''}>
                  <a href="#skills" onClick={(e) => { e.preventDefault(); scrollToSection('skills'); }}>
                    <span className="nav-icon">⚡</span>
                    <span className="nav-text">Skills</span>
                  </a>
                </li>
                <li className={activeSection === 'freelance-work' ? 'active' : ''}>
                  <a href="#freelance-work" onClick={(e) => { e.preventDefault(); scrollToSection('freelance-work'); }}>
                    <span className="nav-icon">🚀</span>
                    <span className="nav-text">Services</span>
                  </a>
                </li>
                <li className={activeSection === 'contact' ? 'active' : ''}>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                    <span className="nav-icon">📧</span>
                    <span className="nav-text">Contact</span>
                  </a>
                </li>
              </ul>
            </nav>
            <div className="top-nav-actions">
              <button 
                className="nav-toggle show-on-mobile"
                onClick={() => setNavOpen(!navOpen)}
                aria-label="Toggle Navigation"
              >
                <span className={`hamburger ${navOpen ? 'active' : ''}`}></span>
              </button>
            </div>
          </div>
          {navOpen && (
            <div className="mobile-nav-menu show-on-mobile">
              <ul className="top-nav-links">
                <li className={activeSection === 'about' ? 'active' : ''}>
                  <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>👤 About</a>
                </li>
                <li className={activeSection === 'experience' ? 'active' : ''}>
                  <a href="#experience" onClick={(e) => { e.preventDefault(); scrollToSection('experience'); }}>💼 Experience</a>
                </li>
                <li className={activeSection === 'skills' ? 'active' : ''}>
                  <a href="#skills" onClick={(e) => { e.preventDefault(); scrollToSection('skills'); }}>⚡ Skills</a>
                </li>
                <li className={activeSection === 'freelance-work' ? 'active' : ''}>
                  <a href="#freelance-work" onClick={(e) => { e.preventDefault(); scrollToSection('freelance-work'); }}>🚀 Services</a>
                </li>
                <li className={activeSection === 'contact' ? 'active' : ''}>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>📧 Contact</a>
                </li>
              </ul>
            </div>
          )}
        </header>

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-grid">
            <div className="hero-left">
              <div className="hero-badge">Senior Network & Security Engineer</div>
              <h1 className="hero-title">Selvakumar Panneerselvam</h1>
              <p className="hero-subtitle">
                Building secure, reliable, and scalable networks for enterprises across the UAE. Zero Trust, NAC, Wi-Fi, and cloud connectivity specialist.
              </p>
              <div className="hero-chips">
                <span className="chip">Zero Trust (Zscaler)</span>
                <span className="chip">NAC (Aruba ClearPass)</span>
                <span className="chip">Wi-Fi (Aruba Central)</span>
                <span className="chip">Cloud (AWS)</span>
              </div>
              <div className="hero-cta">
                <button 
                  onClick={() => scrollToSection('about')} 
                  className="cta-button primary"
                >
                  View profile
                </button>
                <button 
                  onClick={() => scrollToSection('contact')} 
                  className="cta-button secondary"
                >
                  Book a call
                </button>
                <a 
                  href="/resume.pdf" 
                  download="Selvakumar_Panneerselvam_Resume.pdf"
                  className="cta-button outline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📄 Download Resume
                </a>
              </div>
              <div className="hero-meta">
                <div className="meta-item">📍 Dubai, UAE</div>
                <div className="meta-item">🛡️ 9+ yrs | 99.9% uptime</div>
                <div className="meta-item">✅ Available for engagements</div>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-card">
                <div className="hero-card-header">
                  <span className="hero-card-title">Network Reliability</span>
                  <span className="hero-card-pill">SLA 99.9%</span>
                </div>
                <ul className="hero-card-list">
                  <li><strong>Zero Trust rollout:</strong> Zscaler ZPA/ZIA for secure app & internet access</li>
                  <li><strong>NAC:</strong> Aruba ClearPass with policy-based access & device profiling</li>
                  <li><strong>Wi-Fi at scale:</strong> Aruba Central with AP 635/625/615 across sites</li>
                  <li><strong>Identity:</strong> Microsoft Entra ID (SSO, MFA, conditional access)</li>
                  <li><strong>Visibility:</strong> Cloudmon + SIEM for proactive detection</li>
                </ul>
                <div className="hero-stats">
                  <div className="stat">
                    <div className="stat-value">40%</div>
                    <div className="stat-label">Faster IR</div>
                  </div>
                  <div className="stat">
                    <div className="stat-value">60%</div>
                    <div className="stat-label">Attack surface ↓</div>
                  </div>
                  <div className="stat">
                    <div className="stat-value">99.9%</div>
                    <div className="stat-label">Uptime</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="main-content">
          {/* About Section */}
          <section id="about" className="content-section">
            <div className="section-header">
              <div className="section-number">01</div>
              <h2 className="section-title">About Me</h2>
              <p className="section-subtitle">
                Certified Network & Security Administrator with enterprise-level expertise
              </p>
            </div>
            
            <div className="about-content">
              <div className="professional-summary">
                <div className="summary-card">
                  <h3>Professional Summary</h3>
                  <p>
                    Certified Network & Security Administrator with <strong>9+ years of experience</strong> managing enterprise-level IT infrastructure in high-demand environments, including <strong>Expo 2020</strong>. Proven expertise in network security, firewalls (Fortinet NGFW), VLANs, VPNs, AWS cloud services, and MDM solutions. Strong background in IT audits, compliance, and infrastructure optimization. Adept at delivering <strong>99.9% uptime</strong> and leading successful hybrid cloud migrations.
                  </p>
                </div>
              </div>

              <div className="story-cards">
                <div className="story-card">
                  <div className="story-icon">🛡️</div>
                  <h3>The Security Architect</h3>
                  <p>As a Senior Network Engineer at Meydan Freezone, I design and implement cutting-edge security architectures. My expertise in next-generation firewalls, zero-trust networking, and compliance frameworks ensures robust protection for critical infrastructure.</p>
                </div>
                
                <div className="story-card">
                  <div className="story-icon">🔒</div>
                  <h3>The Security Strategist</h3>
                  <p>With expertise in security frameworks like NIST and ISO 27001, I develop comprehensive security strategies that protect against evolving threats. My approach combines technical controls with security awareness to create a culture of security.</p>
                </div>
                
                <div className="story-card">
                  <div className="story-icon">🌐</div>
                  <h3>The Network Architect</h3>
                  <p>Specializing in designing high-availability network infrastructures with security-first principles. My expertise spans from enterprise routing and switching to software-defined networking and cloud connectivity solutions.</p>
                </div>
              </div>

              <div className="highlight-cards">
                <div className="highlight-card">
                  <h4>Security Leadership</h4>
                  <p>Leading security initiatives at Meydan Freezone with a focus on enterprise protection</p>
                </div>
                <div className="highlight-card">
                  <h4>Security Frameworks</h4>
                  <p>Expertise in NIST, ISO 27001, and Zero Trust architectures</p>
                </div>
                <div className="highlight-card">
                  <h4>Network Defense</h4>
                  <p>Advanced threat detection and incident response capabilities</p>
                </div>
                <div className="highlight-card">
                  <h4>Performance Driven</h4>
                  <p>Proven track record of 99.9% uptime and 40% downtime reduction</p>
                </div>
              </div>
            </div>
          </section>

          {/* Experience Section */}
          <section id="experience" className="content-section">
            <div className="section-header">
              <div className="section-number">02</div>
              <h2 className="section-title">Professional Journey</h2>
              <p className="section-subtitle">
                9+ years of enterprise-level network security and infrastructure management across high-demand environments
              </p>
            </div>
            
            <div className="experience-timeline">
              <div className="experience-item">
                <div className="experience-header">
                  <h3>Senior Network Engineer</h3>
                  <div className="company-info">
                    <span className="company">Meydan Freezone, Dubai</span>
                    <span className="duration">Sep 2025 – Present</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Lead the design and implementation of enterprise-grade secure network infrastructure for Meydan Freezone, ensuring 99.99% uptime and robust defense-in-depth security posture</li>
                    <li>Architected and deployed advanced security solutions including FortiGate NGFW, IDS/IPS, SIEM integration, and zero-trust network segmentation strategies</li>
                    <li>Implemented and managed Zero Trust security architecture using Zscaler ZPA (Zero Trust Network Access) and ZIA (Zero Trust Internet Access) to provide secure access to applications and internet resources without traditional VPN</li>
                    <li>Deployed and configured Aruba ClearPass for Network Access Control (NAC), implementing policy-based access control, device profiling, and dynamic VLAN assignment to ensure only authorized devices connect to the network</li>
                    <li>Managed enterprise wireless infrastructure using Aruba Central cloud management platform, deploying and maintaining Aruba AP 635, AP625, and AP615 access points across multiple locations with centralized monitoring and configuration</li>
                    <li>Implemented Microsoft Entra ID (Azure AD) for identity and access management, configuring SSO, conditional access policies, and multi-factor authentication to enhance security posture</li>
                    <li>Deployed Sophos XDR (Extended Detection and Response) for advanced endpoint security, providing comprehensive threat detection, investigation, and response capabilities across endpoints, servers, and cloud workloads</li>
                    <li>Configured and managed Perception Point email gateway security solution, implementing advanced threat protection, anti-phishing, and email security policies to protect against email-borne threats</li>
                    <li>Utilized Cloudmon for comprehensive network monitoring and performance management, enabling proactive issue detection and ensuring optimal network performance across the enterprise infrastructure</li>
                    <li>Develop and enforce comprehensive security policies, procedures, and standards in alignment with ISO 27001, NIST CSF, and PCI-DSS compliance frameworks</li>
                    <li>Conduct regular security assessments, vulnerability scans, and penetration testing using industry-standard tools to identify and remediate security risks</li>
                    <li>Implemented advanced threat detection and response mechanisms, reducing incident response time by 40%</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>Network Security Engineer</h3>
                  <div className="company-info">
                    <span className="company">Ekar Car Rental LLC, Dubai</span>
                    <span className="duration">Apr 2022 – June 2025</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Spearheaded the security architecture design and implementation, deploying and optimizing Fortinet NGFW (100F, 101F) firewalls, FortiAnalyzer, and SIEM integration for comprehensive threat detection and response</li>
                    <li>Architected and implemented a zero-trust network access (ZTNA) model, enhancing security posture and reducing attack surface by 60%</li>
                    <li>Led the implementation of Google Workspace and Microsoft 365 security controls including DLP, ATP, and conditional access policies, reducing security incidents by 75%</li>
                    <li>Conducted security risk assessments and penetration tests, identifying and remediating critical vulnerabilities across network infrastructure and cloud environments</li>
                    <li>Established and maintained security documentation including risk assessments, security policies, and incident response plans in compliance with ISO 27001 and NIST frameworks</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>IT Support Engineer</h3>
                  <div className="company-info">
                    <span className="company">Expo 2020 (via Jumbo Manpower LLC), Dubai</span>
                    <span className="duration">Sep 2021 – Apr 2022</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Provided critical security support and incident response for 200+ users across multiple pavilions during the high-profile international event</li>
                    <li>Implemented and enforced security protocols, including access control, network segmentation, and endpoint protection measures</li>
                    <li>Conducted security awareness training for staff, reducing phishing susceptibility by 60%</li>
                    <li>Resolved security incidents via Jira & ManageEngine while maintaining 99.9% SLA compliance</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>Network Security Administrator</h3>
                  <div className="company-info">
                    <span className="company">Suninfosys Technologies, Dubai</span>
                    <span className="duration">Jul 2019 – Sep 2021</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Designed and implemented secure network architectures for enterprise clients, incorporating defense-in-depth strategies and security best practices</li>
                    <li>Managed and hardened Windows Server environments, including Group Policy implementation and security baselining</li>
                    <li>Administered and secured network infrastructure including FortiGate firewalls, VPN concentrators, and enterprise Wi-Fi solutions</li>
                    <li>Conducted security assessments and implemented remediation plans, addressing critical vulnerabilities and compliance requirements</li>
                    <li>Reduced security incidents by 50% through implementation of advanced monitoring and alerting systems</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>IT Support Engineer</h3>
                  <div className="company-info">
                    <span className="company">Unique Power Technical Service LLC, Al Ain</span>
                    <span className="duration">Jul 2018 – Dec 2018</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Designed and implemented secure remote access solutions using IPsec and SSL VPN technologies</li>
                    <li>Hardened network infrastructure through firewall rule optimization, access control policies, and security best practices</li>
                    <li>Deployed and configured security monitoring tools including SIEM solutions for real-time threat detection</li>
                    <li>Conducted security awareness training for staff and implemented security policies and procedures</li>
                    <li>Reduced security vulnerabilities by 65% through systematic patching and configuration management</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>IT Security Support Specialist</h3>
                  <div className="company-info">
                    <span className="company">GG INFOTECH, Chennai</span>
                    <span className="duration">Nov 2016 – Feb 2018</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Implemented and managed endpoint security solutions including antivirus, encryption, and endpoint detection and response (EDR) tools</li>
                    <li>Conducted vulnerability scans and security assessments, prioritizing and remediating critical security issues</li>
                    <li>Managed security patching and updates across 100+ endpoints, maintaining 98% patch compliance</li>
                    <li>Developed and maintained security documentation including incident response plans and security procedures</li>
                    <li>Reduced security incidents by 40% through proactive monitoring and security controls implementation</li>
                  </ul>
                </div>
              </div>

              <div className="experience-item">
                <div className="experience-header">
                  <h3>Network Security Technician</h3>
                  <div className="company-info">
                    <span className="company">Nayan Communication, Chennai</span>
                    <span className="duration">Jan 2016 – Oct 2016</span>
                  </div>
                </div>
                <div className="experience-content">
                  <ul>
                    <li>Implemented and maintained network security controls for wireless and wired network infrastructure</li>
                    <li>Conducted security assessments of network infrastructure and recommended security enhancements</li>
                    <li>Managed a team of field engineers, ensuring security best practices were followed during installations and maintenance</li>
                    <li>Developed and maintained network security documentation and standard operating procedures</li>
                    <li>Responded to security incidents, performed root cause analysis, and implemented corrective actions</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Skills Section */}
          <section id="skills" className="content-section">
            <div className="section-header">
              <div className="section-number">03</div>
              <h2 className="section-title">Core Technical Skills</h2>
              <p className="section-subtitle">
                Comprehensive expertise across enterprise network security, cloud platforms, and infrastructure management
              </p>
            </div>
            
            <div className="skills-showcase">
              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🛡️</div>
                  <h3>Network Security</h3>
                  <p>Enterprise firewall & VPN solutions</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Fortinet NGFW (30D, 40F, 100F, 101F)</span>
                  <span className="tech-tag expert">Aruba ClearPass (NAC)</span>
                  <span className="tech-tag expert">IPSec VPN</span>
                  <span className="tech-tag expert">SSL VPN</span>
                  <span className="tech-tag expert">Port Forwarding</span>
                  <span className="tech-tag advanced">VLAN Security</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🔗</div>
                  <h3>Switching & Routing</h3>
                  <p>Network infrastructure backbone</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">FortiSwitch 148F/124F</span>
                  <span className="tech-tag expert">Aruba 1930</span>
                  <span className="tech-tag advanced">UniFi USW</span>
                  <span className="tech-tag advanced">D-Link PoE</span>
                  <span className="tech-tag expert">VLAN Configuration</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🖥️</div>
                  <h3>Servers & Operating Systems</h3>
                  <p>Enterprise server management</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Windows Server 2008–2022</span>
                  <span className="tech-tag expert">SQL Server (Std & Express)</span>
                  <span className="tech-tag expert">Active Directory</span>
                  <span className="tech-tag advanced">Domain Management</span>
                  <span className="tech-tag advanced">Group Policy</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">☁️</div>
                  <h3>Cloud Services (AWS)</h3>
                  <p>Scalable cloud infrastructure</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">EC2</span>
                  <span className="tech-tag expert">IAM</span>
                  <span className="tech-tag expert">S3</span>
                  <span className="tech-tag expert">RDS</span>
                  <span className="tech-tag advanced">Route 53</span>
                  <span className="tech-tag advanced">VPC</span>
                  <span className="tech-tag intermediate">CloudFormation</span>
                  <span className="tech-tag advanced">ELB</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">📱</div>
                  <h3>MDM & Identity Management</h3>
                  <p>Device & identity security</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Microsoft Entra ID (Azure AD)</span>
                  <span className="tech-tag expert">JumpCloud</span>
                  <span className="tech-tag expert">Apple Business Manager</span>
                  <span className="tech-tag advanced">SSO Integration</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">📧</div>
                  <h3>Collaboration & Email</h3>
                  <p>Enterprise communication platforms</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Microsoft 365</span>
                  <span className="tech-tag expert">Google Workspace</span>
                  <span className="tech-tag expert">Perception Point Email Gateway</span>
                  <span className="tech-tag advanced">cPanel</span>
                  <span className="tech-tag advanced">Exchange Server</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">📡</div>
                  <h3>Wireless Networks</h3>
                  <p>Enterprise wireless solutions</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Aruba Central</span>
                  <span className="tech-tag expert">Aruba AP 635</span>
                  <span className="tech-tag expert">Aruba AP 625</span>
                  <span className="tech-tag expert">Aruba AP 615</span>
                  <span className="tech-tag expert">Aruba AP22</span>
                  <span className="tech-tag expert">Ubiquiti (AC-HD, AC-Pro, AC-LR)</span>
                  <span className="tech-tag expert">Fortinet 431F</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">📊</div>
                  <h3>Monitoring & Tools</h3>
                  <p>Network performance & management</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Cloudmon</span>
                  <span className="tech-tag expert">OpManager</span>
                  <span className="tech-tag expert">Nagios</span>
                  <span className="tech-tag advanced">SolarWinds</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🎫</div>
                  <h3>Helpdesk Platforms</h3>
                  <p>IT service management</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Jira</span>
                  <span className="tech-tag expert">ManageEngine</span>
                  <span className="tech-tag advanced">Freshdesk</span>
                  <span className="tech-tag advanced">Zendesk</span>
                  <span className="tech-tag advanced">ITSM</span>
                  <span className="tech-tag advanced">Zohodesk</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🔐</div>
                  <h3>VPN Clients</h3>
                  <p>Secure remote access</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Cisco AnyConnect</span>
                  <span className="tech-tag expert">FortiClient VPN</span>
                </div>
              </div>

              <div className="skill-category">
                <div className="category-header">
                  <div className="category-icon">🔒</div>
                  <h3>Zero Trust & Endpoint Security</h3>
                  <p>Advanced security architectures</p>
                </div>
                <div className="tech-tags">
                  <span className="tech-tag expert">Zscaler ZPA (Zero Trust Network Access)</span>
                  <span className="tech-tag expert">Zscaler ZIA (Zero Trust Internet Access)</span>
                  <span className="tech-tag expert">Sophos XDR (Extended Detection & Response)</span>
                </div>
              </div>
            </div>
          </section>

          {/* Certifications Section */}
          <section id="certifications" className="content-section">
            <div className="section-header">
              <div className="section-number">04</div>
              <h2 className="section-title">Certifications</h2>
              <p className="section-subtitle">
                Professional credentials and continuous learning journey
              </p>
            </div>
            
            <div className="certifications-grid">
              <div className="cert-card completed">
                <div className="cert-icon">🌐</div>
                <h3>Cisco Certified Network Associate (CCNA)</h3>
                <p className="cert-status">Certified</p>
                <p className="cert-description">Comprehensive networking fundamentals and routing/switching expertise</p>
              </div>
              
              <div className="cert-card completed">
                <div className="cert-icon">🛡️</div>
                <h3>Fortinet NSE 1 & NSE 2</h3>
                <p className="cert-status">Certified</p>
                <p className="cert-description">Network Security Expert certification for Fortinet security solutions</p>
              </div>
              
              <div className="cert-card in-progress">
                <div className="cert-icon">🔒</div>
                <h3>ISC2-CC (Certified in Cybersecurity)</h3>
                <p className="cert-description">cybersecurity certification from (ISC)²</p>
              </div>
              
              <div className="cert-card in-progress">
                <div className="cert-icon">🔍</div>
                <h3>SC-200 Microsoft Security Operations Analyst</h3>
                <p className="cert-description">Microsoft SOC analyst certification for threat detection and response</p>
              </div>
            </div>
          </section>

          {/* Freelance Work Section */}
          <section id="freelance-work" className="content-section">
            <div className="section-header">
              <div className="section-number">05</div>
              <h2 className="section-title">Freelance Services</h2>
              <p className="section-subtitle">
                Professional consulting and implementation services for network security and infrastructure
              </p>
            </div>
            
            <div className="section-card">
              <p className="mb-4">professional freelance consulting in the following areas:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="skill-item">
                  <div className="title">🛡️ NETWORK SECURITY</div>
                  <div className="content">
                    • Firewall configuration & management<br/>
                    • VPN setup & maintenance<br/>
                    • Security policy implementation<br/>
                    • Network security audits
                  </div>
                </div>
                <div className="skill-item">
                  <div className="title">☁️ CLOUD SERVICES</div>
                  <div className="content">
                    • AWS infrastructure setup<br/>
                    • Cloud migration consulting<br/>
                    • IAM & security configuration<br/>
                    • Cloud cost optimization
                  </div>
                </div>
                <div className="skill-item">
                  <div className="title">🔗 NETWORK INFRASTRUCTURE</div>
                  <div className="content">
                    • Network design & implementation<br/>
                    • VLAN & routing configuration<br/>
                    • Switch & access point setup<br/>
                    • Network monitoring & troubleshooting
                  </div>
                </div>
                <div className="skill-item">
                  <div className="title">📱 MDM & ENDPOINT MANAGEMENT</div>
                  <div className="content">
                    • Jumpcloud implementation<br/>
                    • Microsoft Intune setup<br/>
                    • Apple Business Manager<br/>
                    • SSO configuration
                  </div>
                </div>
              </div>
            </div>
            <div className="section-card">
              <h4 className="text-accent mb-4">Why Choose Me?</h4>
              <div className="why-grid">
                <div className="why-item">
                  <div className="why-icon">⏳</div>
                  <div className="why-title">9+ Years Experience</div>
                  <div className="why-desc">Enterprise-scale network & security</div>
                </div>
                <div className="why-item">
                  <div className="why-icon">🎓</div>
                  <div className="why-title">CCNA Certified</div>
                  <div className="why-desc">Strong fundamentals in networking</div>
                </div>
                <div className="why-item">
                  <div className="why-icon">🛡️</div>
                  <div className="why-title">Fortinet NSE1 & NSE2</div>
                  <div className="why-desc">Hands-on with Fortinet ecosystems</div>
                </div>
                <div className="why-item">
                  <div className="why-icon">🏢</div>
                  <div className="why-title">Enterprise Proven</div>
                  <div className="why-desc">Delivered reliable, secure systems</div>
                </div>
                <div className="why-item">
                  <div className="why-icon">🌍</div>
                  <div className="why-title">Flexible Engagements</div>
                  <div className="why-desc">Remote + on-site, quick turnarounds</div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="content-section">
            <div className="section-header">
              <div className="section-number">// 05</div>
              <h2 className="section-title">Get In Touch</h2>
              <p className="section-subtitle">
                Ready to discuss your project? Let's connect and explore how I can help secure your infrastructure
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Form */}
              <div className="section-card">
                <h3 className="text-accent mb-4">Send Message</h3>
                <p className="mb-4">Interested in working together? Send me a message and I'll get back to you within 24 hours.</p>
                {formStatus === 'success' && (
                  <div className="text-accent border border-green-500 bg-green-50 p-4 mb-4 rounded-lg">
                    ✅ Message sent successfully! I'll get back to you soon.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-red-400 border border-red-400 p-4 mb-4 rounded-lg">
                    ❌ Failed to send message. Please contact me directly at: <br/>
                    📧 <a href="mailto:selva.enquiry@gmail.com?subject=Freelance Inquiry" className="text-accent underline">selva.enquiry@gmail.com</a>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name" 
                    required
                    className="input-field"
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your Email" 
                    required
                    className="input-field"
                  />
                  <input 
                    type="text" 
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Subject (e.g., Freelance Project, Consultation)" 
                    required
                    className="input-field"
                  />
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell me about your project requirements..." 
                    rows="4"
                    required
                    className="input-field"
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={formStatus === 'sending'}
                    className="btn-primary w-full"
                  >
                    {formStatus === 'sending' ? 'SENDING...' : 'SEND ENQUIRY'}
                  </button>
                </form>
              </div>
              {/* Contact Info */}
              <div className="section-card">
                <h3 className="text-accent mb-6">Contact Information</h3>
                <div className="contact-info-grid">
                  <div className="contact-item">
                    <div className="contact-icon-wrapper">
                      <div className="contact-icon">📧</div>
                    </div>
                    <div className="contact-content">
                      <h4 className="contact-label">Email</h4>
                      <a href="mailto:selvaatr94@gmail.com" className="contact-value link-clean">selvaatr94@gmail.com</a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper">
                      <div className="contact-icon">💼</div>
                    </div>
                    <div className="contact-content">
                      <h4 className="contact-label">LinkedIn</h4>
                      <a href="https://www.linkedin.com/in/selvakumar-panneerselvam-9a420411a/" target="_blank" rel="noopener noreferrer" className="contact-value link-clean">View Profile</a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper">
                      <div className="contact-icon">🌍</div>
                    </div>
                    <div className="contact-content">
                      <h4 className="contact-label">Location</h4>
                      <p className="contact-value">Dubai, United Arab Emirates</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper">
                      <div className="contact-icon">🛡️</div>
                    </div>
                    <div className="contact-content">
                      <h4 className="contact-label">Role</h4>
                      <p className="contact-value">Network & Security Engineer</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper">
                      <div className="contact-icon">⏰</div>
                    </div>
                    <div className="contact-content">
                      <h4 className="contact-label">Availability</h4>
                      <p className="contact-value">Available for freelance projects</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="footer-clean">
            <p>© 2024 SELVAKUMAR PANNEERSELVAM. ALL RIGHTS RESERVED.</p>
          </footer>
        </main>
      </div>
    </div>
  );
}
